import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { ArrowLeft, Save, Trash2 } from 'lucide-react';
import { useDatasetItem, useUpdateDatasetItem, useDeleteDatasetItem } from '@/hooks/useEvaluator';
import { toast } from '@/hooks/use-toast';
import {
  isStructuredPersona,
  PERSONA_AGE_RANGES,
  type PersonaAgeRange,
  type StructuredPersona,
} from '@/types/evaluator';

interface DatasetItemDetailProps {
  orgId: string;
  datasetName: string;
  itemId: string;
  onBack: () => void;
}

interface StructuredFormState {
  name: string;
  title: string;
  age_range: PersonaAgeRange;
  company: string;
  industry: string;
  goal: string;
  pain_points: string;
  constraints: string;
}

const EMPTY_STRUCTURED: StructuredFormState = {
  name: '',
  title: '',
  age_range: PERSONA_AGE_RANGES[1],
  company: '',
  industry: '',
  goal: '',
  pain_points: '',
  constraints: '',
};

function readString(input: Record<string, unknown> | undefined, key: string): string {
  const value = input?.[key];
  return typeof value === 'string' ? value : '';
}

function readEnum<T extends string>(
  input: Record<string, unknown> | undefined,
  key: string,
  allowed: readonly T[],
  fallback: T,
): T {
  const value = input?.[key];
  return typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : fallback;
}

function structuredFormFromInput(input: StructuredPersona): StructuredFormState {
  return {
    name: readString(input, 'name'),
    title: readString(input, 'title'),
    age_range: readEnum(input, 'age_range', PERSONA_AGE_RANGES, PERSONA_AGE_RANGES[1]),
    company: readString(input, 'company'),
    industry: readString(input, 'industry'),
    goal: readString(input, 'goal'),
    pain_points: readString(input, 'pain_points'),
    constraints: readString(input, 'constraints'),
  };
}

function structuredFormHasChanges(form: StructuredFormState, original: StructuredFormState): boolean {
  return (Object.keys(original) as (keyof StructuredFormState)[]).some(
    (key) => form[key] !== original[key],
  );
}

function validateStructured(form: StructuredFormState): string | null {
  if (!form.name.trim()) return 'Name is required';
  if (!form.title.trim()) return 'Title is required';
  if (!form.company.trim()) return 'Company is required';
  if (!form.industry.trim()) return 'Industry is required';
  if (!form.goal.trim()) return 'Scenario is required';
  if (!form.pain_points.trim()) return 'Motivations and concerns are required';
  return null;
}

export function DatasetItemDetail({ orgId, datasetName, itemId, onBack }: DatasetItemDetailProps) {
  const { data: item, isLoading, error } = useDatasetItem(datasetName, itemId, orgId);
  const updateMutation = useUpdateDatasetItem();
  const deleteMutation = useDeleteDatasetItem();

  const [structuredForm, setStructuredForm] = useState<StructuredFormState>(EMPTY_STRUCTURED);
  const [originalStructured, setOriginalStructured] = useState<StructuredFormState>(EMPTY_STRUCTURED);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!item || initialized) return;
    if (item.input) {
      const next = structuredFormFromInput(item.input as StructuredPersona);
      setStructuredForm(next);
      setOriginalStructured(next);
    }
    setInitialized(true);
  }, [item, initialized]);

  const validationError = useMemo(
    () => validateStructured(structuredForm),
    [structuredForm],
  );

  const hasChanges = structuredFormHasChanges(structuredForm, originalStructured);

  const isLegacy = !!item && !isStructuredPersona(item.input as StructuredPersona);
  const legacyPersonaText = isLegacy
    ? readString(item?.input as Record<string, unknown> | undefined, 'persona')
    : '';

  const canSave = initialized
    && hasChanges
    && !updateMutation.isPending
    && !validationError;

  const headerTitle = (() => {
    if (!item) return 'Persona';
    const name = readString(item.input as Record<string, unknown>, 'name');
    const title = readString(item.input as Record<string, unknown>, 'title');
    const company = readString(item.input as Record<string, unknown>, 'company');
    const left = name || 'Persona';
    const right = [title, company].filter(Boolean).join(' @ ');
    return right ? `${left} · ${right}` : left;
  })();

  const handleSave = async () => {
    if (!item) return;
    if (validationError) {
      toast({ title: 'Cannot save', description: validationError, variant: 'destructive' });
      return;
    }

    const nextInput = { ...item.input, ...structuredForm };

    try {
      await updateMutation.mutateAsync({
        datasetName,
        itemId,
        organizationId: orgId,
        data: { input: nextInput },
      });
      setOriginalStructured(structuredForm);
      toast({ title: 'Success', description: 'Item updated.' });
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to update item.',
        variant: 'destructive',
      });
    }
  };

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading item...</p>;
  }

  if (error || !item) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        <p className="text-sm text-destructive">Failed to load persona.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <Button variant="ghost" size="icon" onClick={onBack} className="shrink-0 mt-0.5">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-foreground line-clamp-2">{headerTitle}</h2>
            <p className="text-xs text-muted-foreground">
              {datasetName}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                disabled={deleteMutation.isPending}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete item</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently delete this persona. This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  onClick={() => {
                    deleteMutation.mutate(
                      { datasetName, itemId, organizationId: orgId },
                      {
                        onSuccess: () => {
                          toast({ title: 'Deleted', description: 'Item has been deleted.' });
                          onBack();
                        },
                        onError: (err) => {
                          toast({
                            title: 'Error',
                            description: err instanceof Error ? err.message : 'Failed to delete item.',
                            variant: 'destructive',
                          });
                        },
                      },
                    );
                  }}
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <Button size="sm" onClick={handleSave} disabled={!canSave}>
            <Save className="mr-2 h-4 w-4" />
            {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      <div className="space-y-6">
        {isLegacy && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-200">
            <p className="font-semibold">Legacy persona</p>
            <p className="mt-1">
              This item uses the old free-form schema. Fill in the structured fields below to migrate it.
              The original content is preserved until you save.
            </p>
            {legacyPersonaText && (
              <pre className="mt-2 whitespace-pre-wrap break-words font-sans text-xs opacity-90">
                {legacyPersonaText}
              </pre>
            )}
          </div>
        )}
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="persona-name">Name</Label>
            <Input
              id="persona-name"
              value={structuredForm.name}
              maxLength={120}
              onChange={(e) => setStructuredForm((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="e.g., Sarah Chen"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="persona-title">Title</Label>
            <Input
              id="persona-title"
              value={structuredForm.title}
              maxLength={160}
              onChange={(e) => setStructuredForm((prev) => ({ ...prev, title: e.target.value }))}
              placeholder="e.g., VP of Marketing"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="persona-age">Age range</Label>
            <Select
              value={structuredForm.age_range}
              onValueChange={(value) =>
                setStructuredForm((prev) => ({ ...prev, age_range: value as PersonaAgeRange }))
              }
            >
              <SelectTrigger id="persona-age">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PERSONA_AGE_RANGES.map((range) => (
                  <SelectItem key={range} value={range}>
                    {range}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="persona-company">Company</Label>
            <Input
              id="persona-company"
              value={structuredForm.company}
              maxLength={160}
              onChange={(e) => setStructuredForm((prev) => ({ ...prev, company: e.target.value }))}
              placeholder="e.g., Acme Corp"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="persona-industry">Industry</Label>
            <Input
              id="persona-industry"
              value={structuredForm.industry}
              maxLength={120}
              onChange={(e) => setStructuredForm((prev) => ({ ...prev, industry: e.target.value }))}
              placeholder="e.g., SaaS"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="persona-goal">Scenario</Label>
          <Textarea
            id="persona-goal"
            value={structuredForm.goal}
            maxLength={1000}
            onChange={(e) => setStructuredForm((prev) => ({ ...prev, goal: e.target.value }))}
            className="min-h-[90px] whitespace-pre-wrap break-words"
            placeholder="What should this persona try to do in the simulation?"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="persona-pain">Motivations and concerns</Label>
          <Textarea
            id="persona-pain"
            value={structuredForm.pain_points}
            maxLength={1000}
            onChange={(e) => setStructuredForm((prev) => ({ ...prev, pain_points: e.target.value }))}
            className="min-h-[90px] whitespace-pre-wrap break-words"
            placeholder="What is motivating this persona, and what are they worried about?"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="persona-constraints">Behavior rules</Label>
          <Textarea
            id="persona-constraints"
            value={structuredForm.constraints}
            maxLength={1000}
            onChange={(e) => setStructuredForm((prev) => ({ ...prev, constraints: e.target.value }))}
            className="min-h-[90px] whitespace-pre-wrap break-words"
            placeholder="How should this persona behave during the simulation?"
          />
        </div>

        {validationError && (
          <p className="text-xs text-destructive">{validationError}</p>
        )}
      </div>

      {(item.created_at || item.updated_at) && (
        <div className="flex gap-6 text-xs text-muted-foreground pt-2 border-t">
          {item.created_at && <span>Created: {new Date(item.created_at).toLocaleString()}</span>}
          {item.updated_at && <span>Updated: {new Date(item.updated_at).toLocaleString()}</span>}
        </div>
      )}
    </div>
  );
}
